/**
 * `createApiPipelineAlertsSource` — the `trigger-mgmt-api` `AlertsSource`.
 *
 * The shared `createApiAlertsSource` (`@authe/alerts`) is a pre-backend
 * placeholder: it PUT-upserts a camelCase `AlertRule` to `/alert-rules/{id}` and
 * reads a flat array. trigger-mgmt-api actually serves
 * `/v0/{triggers,destinations,alerts}` with **POST-create / PATCH-update**, the
 * snake_case `TriggerRuleRow` / `AlertDestination` shapes, and the standard
 * `{items, next_cursor}` page body. This dashboard-local source crosses that
 * camelCase ⇆ snake_case boundary (mirroring `pipelinesSource` / `tracersSource`)
 * so the rule + destination editors round-trip against the real API. Canonical
 * `/alert-*` paths are mapped onto those `/private/trigger-mgmt-api/v0/*` routes
 * by `RESOURCE_REWRITES` — the canonical names are deliberately unchanged, so
 * moving the surface off pipeline-mgmt-api was a rewrite-table edit.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";

import { walkPages } from "./pagedList";
import { EventCriticality, criticalityFromName } from "@authe/fe-commons/events";
// `anyCondition` is a runtime value: import it from its real module. `@authe/alerts`
// only re-exports the trigger dictionary via `export * from "@authe/fe-commons/triggers"`,
// which esbuild can't surface as a static ESM named export (it works for the CJS
// build only), so a browser/ESM `import { anyCondition } from "@authe/alerts"` throws.
import { anyCondition } from "@authe/fe-commons/triggers";
import {
  type AlertChannel,
  type AlertDestination,
  type AlertDestinationConfig,
  type AlertEvent,
  type AlertHistoryPage,
  type AlertHistoryQuery,
  type AlertRule,
  type AlertsSource,
  type Condition,
  severityToWire,
} from "@authe/alerts";

const RULES_PATH = "/alert-rules";
const DESTINATIONS_PATH = "/alert-destinations";
const HISTORY_PATH = "/alert-history";

// Severity encoding lives in `@authe/alerts` (`severityToWire`), not here. Two
// adapters used to encode it independently — this one with a table that mapped
// the UI-only `SUCCESS` band onto the backend's lowest severity, the package's
// with `criticalityLabel().toLowerCase()`, which produced "success": a value
// `TriggerSeverity` does not have. Same rule, different wire, depending on
// which adapter wrote it.

// -- rule wire ⇄ AlertRule -------------------------------------------------
interface RuleWire {
  id: string;
  name?: string;
  description?: string | null;
  enabled?: boolean;
  severity?: string;
  condition?: Condition;
  destination_ids?: string[];
  cooldown_seconds?: number;
  quiet_hours?: { start_hour?: number; end_hour?: number; timezone?: string } | null;
  muted_until?: number | null;
  visible_to_team_ids?: string[];
}

function ruleFromWire(row: RuleWire): AlertRule {
  return {
    id: row.id,
    name: row.name ?? "",
    description: row.description ?? undefined,
    enabled: row.enabled ?? true,
    severity: criticalityFromName(row.severity ?? undefined, EventCriticality.MEDIUM),
    // An empty/absent condition is the backend "match any" node.
    condition: row.condition ?? anyCondition(),
    destinationIds: row.destination_ids ?? [],
    cooldownSeconds: row.cooldown_seconds ?? 0,
    quietHours: row.quiet_hours
      ? {
          startHour: row.quiet_hours.start_hour ?? 0,
          endHour: row.quiet_hours.end_hour ?? 0,
          timezone: row.quiet_hours.timezone ?? "UTC",
        }
      : undefined,
    mutedUntil: row.muted_until ?? null,
    visibleToTeamIds: row.visible_to_team_ids ?? [],
  };
}

function ruleToWire(rule: AlertRule): Record<string, unknown> {
  return {
    name: rule.name,
    description: rule.description ?? null,
    enabled: rule.enabled,
    severity: severityToWire(rule.severity),
    condition: rule.condition ?? anyCondition(),
    destination_ids: rule.destinationIds,
    cooldown_seconds: rule.cooldownSeconds ?? 0,
    quiet_hours: rule.quietHours
      ? {
          start_hour: rule.quietHours.startHour,
          end_hour: rule.quietHours.endHour,
          timezone: rule.quietHours.timezone,
        }
      : null,
    muted_until: rule.mutedUntil ?? null,
    visible_to_team_ids: rule.visibleToTeamIds ?? [],
  };
}

// -- destination wire ⇄ AlertDestination -----------------------------------
// Backend `DestinationKind`: webhook | sns | email | slack | in_app | connector.
// The FE channel union covers webhook/email/slack/discord; the editors here only
// author those, so the remaining kinds map onto `webhook` for display.
interface DestinationWire {
  id: string;
  name?: string;
  enabled?: boolean;
  kind?: string;
  config?: Record<string, unknown>;
}

/**
 * A comma-separated address list as an array, or `null` when there is none.
 *
 * `null` rather than `[]` so the caller can tell "the field is absent" from
 * "the field is an empty string" and fall back to the legacy key.
 */
function splitRecipients(raw: unknown): string[] | null {
  if (typeof raw !== "string" || raw.trim() === "") return null;
  return raw
    .split(",")
    .map((a) => a.trim())
    .filter(Boolean);
}

function channelFromKind(kind: string | undefined): AlertChannel {
  switch (kind) {
    case "email":
      return "email";
    case "slack":
      return "slack";
    case "connector":
      return "connector";
    // Carried faithfully rather than collapsed onto `webhook`. The default arm
    // used to swallow both, which was not cosmetic: the list's Enabled switch
    // calls `save()` with whatever was read, so toggling an SNS destination
    // rewrote it as `kind: "webhook"` with an empty URL — the topic ARN gone
    // and its alerts silently stopped.
    case "sns":
      return "sns";
    case "in_app":
      return "in_app";
    case "discord":
      return "discord";
    default:
      return "webhook";
  }
}

function destinationConfigFromWire(
  kind: string | undefined,
  config: Record<string, unknown>,
): AlertDestinationConfig {
  switch (channelFromKind(kind)) {
    case "email":
      return {
        channel: "email",
        // `address` is the key `EmailChannel` actually reads (aliases `to` /
        // `email`), and it holds a COMMA-SEPARATED string, not a list — the
        // adapter does `raw.split(",")`. Reading it as a single address meant a
        // two-recipient destination round-tripped into one recipient whose
        // address was "a@x.com, b@y.com".
        //
        // `recipients` is kept as a fallback only because this front end used
        // to write it; nothing on the backend has ever read it.
        recipients: splitRecipients(config.address ?? config.to ?? config.email)
          ?? (Array.isArray(config.recipients) ? (config.recipients as string[]) : []),
      };
    case "slack":
      return {
        channel: "slack",
        webhookUrl: String(config.webhook_url ?? config.url ?? ""),
        channelName: config.channel ? String(config.channel) : undefined,
      };
    case "connector":
      // `connector_id` is the integration's `runtime_id` — the id
      // `ConnectorChannel` looks up in the per-tenancy registry.
      return { channel: "connector", connectorId: String(config.connector_id ?? "") };
    case "sns":
    case "in_app":
      // Verbatim, so the round trip cannot lose anything this app has no field
      // for (a topic ARN today, whatever these kinds grow tomorrow).
      return { channel: channelFromKind(kind) as "sns" | "in_app", raw: { ...config } };
    case "discord":
      return { channel: "discord", webhookUrl: String(config.webhook_url ?? config.url ?? "") };
    case "webhook":
    default:
      return { channel: "webhook", url: String(config.url ?? "") };
  }
}

function destinationFromWire(row: DestinationWire): AlertDestination {
  return {
    id: row.id,
    name: row.name ?? "",
    enabled: row.enabled ?? true,
    config: destinationConfigFromWire(row.kind, row.config ?? {}),
  };
}

function destinationToWire(dest: AlertDestination): Record<string, unknown> {
  const { config } = dest;
  // No initialisers: every arm below assigns both, `default` included, so a
  // seed value is only ever the shape a future arm forgets to overwrite.
  let kind: string;
  let wireConfig: Record<string, unknown>;
  switch (config.channel) {
    case "email":
      kind = "email";
      // `address`, comma-separated — NOT `recipients`. This wrote a key no
      // channel adapter reads, under a shape none of them accept, so every
      // email destination authored here resolved to
      // `ChannelUnusable("no address in config")` at delivery and the alert
      // reached nobody. The rule matched, the history row was written, and the
      // only sign was a `failed` entry in `delivery_status`.
      wireConfig = { address: config.recipients.join(", ") };
      break;
    case "slack":
      kind = "slack";
      wireConfig = { webhook_url: config.webhookUrl, channel: config.channelName };
      break;
    case "connector":
      kind = "connector";
      wireConfig = { connector_id: config.connectorId };
      break;
    case "sns":
    case "in_app":
      kind = config.channel;
      // Written back exactly as read. `name` and `enabled` are still the
      // caller's, so renaming or disabling one of these works; its target does
      // not pass through a shape this app invented.
      wireConfig = { ...config.raw };
      break;
    case "discord":
      // NOT `kind: "discord"` — `DestinationKind` has no such member
      // (`webhook | sns | email | slack | in_app | connector`) and the model
      // validator rejects the POST outright. A Discord webhook is an ordinary
      // HTTP endpoint, so it is delivered as a `webhook`, which works. The
      // editor no longer offers the channel (`ALERT_AUTHORABLE_CHANNELS`);
      // this arm is what stops an older draft round-tripping into a 422.
      kind = "webhook";
      wireConfig = { url: config.webhookUrl };
      break;
    case "webhook":
    default:
      kind = "webhook";
      wireConfig = { url: config.url };
      break;
  }
  return { name: dest.name, enabled: dest.enabled, kind, config: wireConfig };
}

export function createApiPipelineAlertsSource(client: ApiClient): AlertsSource {
  // The editors author a client-side id before the backend mints one, so track
  // the ids the API already knows to pick POST (create) vs PATCH (update).
  const knownRuleIds = new Set<string>();
  const knownDestinationIds = new Set<string>();

  return {
    async listRules() {
      // Paged. `knownRuleIds` below is rebuilt from THIS list and decides
      // POST-vs-PATCH on save, so a rule the list never reached is a rule the
      // editor would re-create rather than update — and two enabled trigger
      // rules on one condition send the alert twice.
      const rows = await walkPages(client, RULES_PATH);
      knownRuleIds.clear();
      for (const row of rows) knownRuleIds.add(String(row.id));
      return rows.map((row) => ruleFromWire(row as unknown as RuleWire));
    },
    async saveRule(rule) {
      const body = ruleToWire(rule);
      const row = knownRuleIds.has(rule.id)
        ? unwrapData(await client.patch<RuleWire>(`${RULES_PATH}/${encodeURIComponent(rule.id)}`, body))
        : unwrapData(await client.post<RuleWire>(RULES_PATH, body));
      const saved = ruleFromWire(row);
      knownRuleIds.add(saved.id);
      // Drop the 30s GET cache so the caller's refetch reflects the write
      // (otherwise the new/updated rule won't appear until the TTL expires).
      // `invalidateMatching`, because `listRules` reads through `walkPages`,
      // which always sends `?limit=100`: the exact-URL form keys on the
      // resolved URL INCLUDING the query string, so naming the bare path
      // matched nothing and the refetch was served the pre-write page.
      client.invalidateMatching(RULES_PATH);
      return saved;
    },
    async deleteRule(id) {
      // `unwrapData`: the client resolves an `{ok:false}` envelope rather than
      // throwing, so a bare `await` here made the optimistic-restore `catch` in
      // `@authe/alerts`'s `useCollection.remove` dead code — the row vanished
      // from the list and the rule went on firing.
      unwrapData(await client.delete<void>(`${RULES_PATH}/${encodeURIComponent(id)}`));
      knownRuleIds.delete(id);
      client.invalidateMatching(RULES_PATH);
    },

    async listDestinations() {
      // Paged, for the same reason as `listRules` one level down in
      // consequence: a destination past the first page saves as a duplicate,
      // and then every alert routed there is delivered twice.
      const rows = await walkPages(client, DESTINATIONS_PATH);
      knownDestinationIds.clear();
      for (const row of rows) knownDestinationIds.add(String(row.id));
      return rows.map((row) => destinationFromWire(row as unknown as DestinationWire));
    },
    async saveDestination(dest) {
      const body = destinationToWire(dest);
      const row = knownDestinationIds.has(dest.id)
        ? unwrapData(
            await client.patch<DestinationWire>(
              `${DESTINATIONS_PATH}/${encodeURIComponent(dest.id)}`,
              body,
            ),
          )
        : unwrapData(await client.post<DestinationWire>(DESTINATIONS_PATH, body));
      const saved = destinationFromWire(row);
      knownDestinationIds.add(saved.id);
      // `invalidateMatching` for the same `?limit=100` reason as the rules list.
      client.invalidateMatching(DESTINATIONS_PATH);
      return saved;
    },
    async deleteDestination(id) {
      unwrapData(
        await client.delete<void>(`${DESTINATIONS_PATH}/${encodeURIComponent(id)}`),
      );
      knownDestinationIds.delete(id);
      client.invalidateMatching(DESTINATIONS_PATH);
    },

    async acknowledgeAlert(id: string): Promise<AlertEvent> {
      // `POST /v0/alerts/{id}/acknowledge` — the rewrite is a pure prefix swap
      // onto trigger-mgmt-api's greedy `{proxy+}`, so the sub-path rides along.
      const row = unwrapData(
        await client.post<AlertEvent>(
          `${HISTORY_PATH}/${encodeURIComponent(id)}/acknowledge`,
          {},
        ),
      );
      // The feed is a 30s-cached GET read through `walkPages` (`?limit=100`),
      // so the exact-URL form would miss it — same reason as the rules list.
      // Without this the reader acknowledges a row, navigates away and back,
      // and finds it unclaimed again.
      client.invalidateMatching(HISTORY_PATH);
      return row;
    },

    async fetchHistory(query: AlertHistoryQuery): Promise<AlertHistoryPage> {
      const page = unwrapData(
        await client.get<{ items?: AlertEvent[]; next_cursor?: string | null }>(HISTORY_PATH, {
          // `rule_id` moves the read onto the by-rule index rather than the
          // tenant's history partition; omitted when absent so the unscoped
          // list keeps its existing cache key.
          query: { cursor: query.cursor, limit: query.limit, rule_id: query.ruleId },
        }),
      );
      return { items: page?.items ?? [], nextCursor: page?.next_cursor ?? null };
    },
  };
}
